/** @jsxImportSource @emotion/react */

import { FC, useMemo, useState } from 'react'
import { Form as FinalForm } from 'react-final-form'
import Button from '@mui/material/Button'
import arrayMutators from 'final-form-arrays'
import Typography from '@mui/material/Typography'
import { FormApi, SubmissionErrors } from 'final-form'
import { useNavigate } from 'react-router-dom'
import { InputField, NumberInputField } from 'components/Input'
import { validateRequired } from 'utils/validation'
import { PriceInput } from 'components/Input/PriceInput'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import { DropzoneField, ImageField } from 'components/DropzoneField'
import { setFieldData } from 'utils/setFieldData'
import * as classes from './styles'

const IMAGE_DROPZONE_OPTIONS = {
  accept: {
    'image/*': [],
  },
}

export type FormValues = {
  type: string
  name: string
  duration: string
  price: number
  description?: string
  originalPrice?: number
  image?: ImageField[]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const validateOriginalPrice = (value?: any, allValues?: object) => {
  const { price } = (allValues ?? {}) as FormValues

  return price != null && Number(value) < Number(price) ? 'Must be greater than or equal to Sale Price' : undefined
}

type Props = {
  getInitialValues: () => FormValues | undefined
  onSubmit: (
    values: FormValues,
    form: FormApi<FormValues>,
    callback?: (errors?: SubmissionErrors) => void
  ) => SubmissionErrors | Promise<SubmissionErrors> | void
  isExtension?: boolean
  isSubscriptionMembership?: boolean
  mode?: 'create' | 'edit'
  onDelete?: () => Promise<void> | void
}

export const Form: FC<Props> = ({
  getInitialValues,
  onSubmit,
  isExtension,
  isSubscriptionMembership,
  mode = 'edit',
  onDelete,
}) => {
  const initialValues = useMemo(getInitialValues, [])
  const navigate = useNavigate()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleConfirmDelete = async () => {
    setIsDeleting(true)

    try {
      await onDelete?.()
      setIsDeleteModalOpen(false)
    } catch {
      // keep the modal open so a failed deletion can be retried without reopening it
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <section css={classes.container}>
      <Typography css={classes.header} variant="title3">
        {mode === 'create' ? 'Create' : 'Edit'} {isExtension ? 'Extension' : 'Membership'} Product
      </Typography>
      <FinalForm<FormValues>
        mutators={{ ...arrayMutators, setFieldData }}
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting }) => (
          <form css={classes.form} onSubmit={handleSubmit}>
            {!isExtension && (
              <InputField name="type" validate={validateRequired} label="Membership Type" disabled fullWidth />
            )}
            <InputField name="name" validate={validateRequired} label="Name" placeholder="Enter Name" fullWidth />
            {isExtension && (
              <InputField name="description" label="Description" placeholder="Enter Description" fullWidth multiline />
            )}
            <NumberInputField
              name="duration"
              validate={validateRequired}
              label={isSubscriptionMembership ? 'Duration' : 'Duration (Days)'}
              placeholder="Enter Duration"
              disabled={isSubscriptionMembership}
              fullWidth
            />
            <PriceInput
              name="price"
              validate={validateRequired}
              label="Sale Price"
              placeholder="Enter Sale Price"
              prefix="$"
              fullWidth
            />
            {isExtension && (
              <PriceInput
                name="originalPrice"
                validate={validateOriginalPrice}
                label="Original Price"
                placeholder="Enter Original Price"
                prefix="$"
                fullWidth
              />
            )}
            {isExtension && (
              <DropzoneField
                name="image"
                validate={validateRequired}
                label="Image"
                dropzoneOptions={IMAGE_DROPZONE_OPTIONS}
                options={{ maxFiles: 1, sliceFromEnd: true }}
              />
            )}
            <div css={classes.buttonsWrapper}>
              {isExtension && mode === 'edit' && (
                <Button
                  variant="outlined"
                  color="red"
                  size="large"
                  disabled={submitting}
                  onClick={() => setIsDeleteModalOpen(true)}
                >
                  Delete
                </Button>
              )}
              <div css={classes.actionButtonsWrapper}>
                <Button variant="outlined" color="blue" size="large" disabled={submitting} onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button variant="contained" type="submit" color="blue" size="large" disabled={submitting}>
                  {mode === 'create' ? 'Create' : 'Update'} Product
                </Button>
              </div>
            </div>
          </form>
        )}
      />
      {isExtension && mode === 'edit' && (
        <ConfirmationModal
          open={isDeleteModalOpen}
          title="Delete Extension"
          subtitle="Are you sure you want to delete this extension product?"
          confirmText="Delete"
          closeText="Cancel"
          onConfirm={handleConfirmDelete}
          onClose={() => setIsDeleteModalOpen(false)}
          isLoading={isDeleting}
        />
      )}
    </section>
  )
}
